import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TranslationService } from '@/layout/service/translation.service';
import { PreparedTenantLogo, prepareTenantLogo } from './prepare-tenant-logo';

@Component({
    selector: 'app-tenant-logo-upload',
    imports: [DialogModule, ButtonModule, MessageModule],
    template: `
        <p-dialog [visible]="true" [modal]="true" [header]="t()('tenant.uploadLogo')" [closable]="!uploading()" [closeOnEscape]="!uploading()" [style]="{ width: '36rem', maxWidth: 'calc(100vw - 2rem)' }" (visibleChange)="close()">
            <p class="text-color-secondary mb-4">{{ tenantName() }} &mdash; {{ t()('tenant.logoUploadHelp') }}</p>
            <input #picker hidden type="file" accept="image/png,image/jpeg,image/webp" (change)="fileSelected($event)" />
            <div
                class="logo-dropzone"
                [class.logo-dropzone--active]="dragActive()"
                role="button"
                tabindex="0"
                [attr.aria-disabled]="uploading()"
                (click)="!uploading() && picker.click()"
                (keydown.enter)="!uploading() && picker.click()"
                (keydown.space)="$event.preventDefault(); !uploading() && picker.click()"
                (dragover)="$event.preventDefault(); dragActive.set(true)"
                (dragleave)="dragActive.set(false)"
                (drop)="drop($event)"
            >
                <i class="fa fa-cloud-arrow-up" aria-hidden="true"></i>
                <strong>{{ t()('tenant.logoDrop') }}</strong>
                <span>{{ t()('tenant.logoFileHint') }}</span>
            </div>
            @if (processing()) {
                <p role="status" class="mt-4"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> {{ t()('tenant.logoProcessing') }}</p>
            }
            @if (previewUrl(); as url) {
                <div class="logo-previews">
                    <div class="logo-preview logo-preview--light"><img [src]="url" [alt]="t()('tenant.logoLightPreview')" /></div>
                    <div class="logo-preview logo-preview--dark"><img [src]="url" [alt]="t()('tenant.logoDarkPreview')" /></div>
                </div>
                <p class="text-sm text-color-secondary mt-3">{{ prepared()?.width }} &times; {{ prepared()?.height }} px &middot; PNG &middot; {{ ((prepared()?.blob?.size ?? 0) / 1024).toFixed(1) }} KB</p>
            }
            @if (error()) {
                <p-message severity="error" class="mt-3">{{ error() }}</p-message>
            }
            <ng-template #footer>
                <button pButton type="button" severity="secondary" [outlined]="true" [disabled]="uploading()" (click)="close()">{{ t()('tenant.cancelUpload') }}</button>
                <button pButton type="button" [loading]="uploading()" [disabled]="uploading() || processing() || !prepared()" (click)="save()">{{ t()('tenant.saveLogo') }}</button>
            </ng-template>
        </p-dialog>
    `,
    styles: `
        .logo-dropzone {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.65rem;
            padding: 1.5rem;
            border: 2px dashed var(--surface-border);
            border-radius: 1rem;
            cursor: pointer;
            text-align: center;
        }
        .logo-dropzone i {
            font-size: 2rem;
            color: var(--primary-color);
        }
        .logo-dropzone span {
            font-size: 0.8rem;
            color: var(--text-color-secondary);
        }
        .logo-dropzone--active,
        .logo-dropzone:focus-visible {
            border-color: var(--primary-color);
            outline: 2px solid var(--primary-color);
            outline-offset: 3px;
        }
        .logo-dropzone[aria-disabled='true'] {
            cursor: wait;
            opacity: 0.6;
        }
        .logo-previews {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-top: 1.5rem;
        }
        .logo-preview {
            display: grid;
            place-items: center;
            min-width: 0;
            height: 10rem;
            padding: 1rem;
            border-radius: 0.75rem;
            border: 1px solid var(--surface-border);
        }
        .logo-preview--light {
            background: #fff;
        }
        .logo-preview--dark {
            background: #18181b;
        }
        .logo-preview img {
            max-width: 100%;
            max-height: 8rem;
            object-fit: contain;
        }
        @media (prefers-reduced-motion: reduce) {
            .fa-spin {
                animation: none;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantLogoUploadComponent {
    readonly namespaceId = input.required<string>();
    readonly tenantName = input.required<string>();
    readonly closed = output<void>();
    readonly t = inject(TranslationService).t;
    private api = inject(CloudApiService);
    readonly dragActive = signal(false);
    readonly processing = signal(false);
    readonly uploading = signal(false);
    readonly prepared = signal<PreparedTenantLogo | null>(null);
    readonly previewUrl = signal<string | null>(null);
    readonly error = signal<string | null>(null);
    private generation = 0;
    private destroyed = false;

    constructor() {
        inject(DestroyRef).onDestroy(() => {
            this.destroyed = true;
            this.generation++;
            this.clearPreview();
        });
    }

    private clearPreview() {
        const url = this.previewUrl();
        if (url) URL.revokeObjectURL(url);
        this.previewUrl.set(null);
        this.prepared.set(null);
    }

    fileSelected(event: Event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        const file = input.files?.[0];
        input.value = '';
        if (file) void this.selectFile(file);
    }

    drop(event: DragEvent) {
        event.preventDefault();
        this.dragActive.set(false);
        if (this.uploading()) return;
        const files = event.dataTransfer?.files;
        if (files?.length !== 1) {
            this.generation++;
            this.clearPreview();
            this.processing.set(false);
            this.error.set(this.t()('tenant.logoOneFile'));
            return;
        }
        void this.selectFile(files[0]);
    }

    async selectFile(file: File) {
        if (this.uploading()) return;
        const generation = ++this.generation;
        this.clearPreview();
        this.error.set(null);
        this.processing.set(true);
        try {
            const prepared = await prepareTenantLogo(file);
            if (generation !== this.generation) return;
            this.prepared.set(prepared);
            this.previewUrl.set(URL.createObjectURL(prepared.blob));
        } catch (error: unknown) {
            if (generation === this.generation) this.error.set(this.t()(error instanceof Error && error.message.startsWith('tenant.') ? error.message : 'tenant.logoReadError'));
        } finally {
            if (generation === this.generation) this.processing.set(false);
        }
    }

    async save() {
        const prepared = this.prepared();
        if (!prepared || this.processing() || this.uploading()) return;
        this.uploading.set(true);
        this.error.set(null);
        try {
            await this.api.uploadNamespaceLogo(this.namespaceId(), prepared.blob);
            if (!this.destroyed) this.closed.emit();
        } catch (error: unknown) {
            if (!this.destroyed) this.error.set(error instanceof HttpErrorResponse && error.status === 400 && typeof error.error === 'string' && error.error.trim() ? error.error : this.t()('tenant.logoSaveError'));
        } finally {
            if (!this.destroyed) this.uploading.set(false);
        }
    }

    close() {
        if (!this.uploading()) this.closed.emit();
    }
}
